import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../../theme/tokens';
import { MOCK_ANALYTICS } from '../crmMockData';

const CHART_HEIGHT = 100; // px inside the bar chart area

function BarChart({ values, maxValue, color, formatLabel }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: { xs: 0.75, sm: 1 },
        height: CHART_HEIGHT,
      }}
      role="img"
      aria-label="Bar chart"
    >
      {values.map((v, idx) => {
        const pct = maxValue > 0 ? (v / maxValue) * 100 : 0;
        return (
          <Box
            key={idx}
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.58rem', mb: 0.5 }}
            >
              {formatLabel(v)}
            </Typography>
            <motion.div
              initial={{ scaleY: 0, originY: 1 }}
              animate={{ scaleY: 1 }}
              transition={{ duration: 0.55, delay: idx * 0.07, ease: 'easeOut' }}
              style={{
                width: '100%',
                originY: 1,
                height: `${Math.max(pct, 4)}%`,
                background: color,
                borderRadius: '4px 4px 0 0',
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

function DayLabels({ days }) {
  return (
    <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 }, mt: 0.5 }}>
      {days.map((d, idx) => (
        <Typography
          key={idx}
          variant="caption"
          sx={{
            flex: 1,
            textAlign: 'center',
            fontSize: '0.6rem',
            color: 'text.secondary',
          }}
        >
          {d}
        </Typography>
      ))}
    </Box>
  );
}

function DemoAnalyticsTab() {
  const [metric, setMetric] = useState('revenue');
  const isRevenue = metric === 'revenue';

  const values = isRevenue ? MOCK_ANALYTICS.revenue : MOCK_ANALYTICS.occupancy;
  const maxValue = Math.max(...values);
  const color = isRevenue ? GRADIENTS.brand : 'linear-gradient(135deg, #0EA5E9 0%, #7DA4D9 100%)';

  const formatLabel = isRevenue
    ? (v) => `₹${(v / 1000).toFixed(1)}k`
    : (v) => `${v}%`;

  const total = isRevenue
    ? `₹${values.reduce((a, b) => a + b, 0).toLocaleString('en-IN')}`
    : `${Math.round(values.reduce((a, b) => a + b, 0) / values.length)}%`;
  const totalLabel = isRevenue ? 'Total this week' : 'Avg occupancy';

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={800}>
          Analytics
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Last 7 days
        </Typography>
      </Box>

      {/* Metric toggle */}
      <ToggleButtonGroup
        value={metric}
        exclusive
        onChange={(_, v) => v && setMetric(v)}
        size="small"
        sx={{ mb: 2, borderRadius: RADIUS.md }}
      >
        <ToggleButton
          value="revenue"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            px: 2,
            borderRadius: `${RADIUS.md}px !important`,
            '&.Mui-selected': {
              background: GRADIENTS.brand,
              color: '#fff',
              '&:hover': { background: GRADIENTS.brandDeep },
            },
          }}
        >
          Revenue
        </ToggleButton>
        <ToggleButton
          value="occupancy"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            px: 2,
            borderRadius: `${RADIUS.md}px !important`,
            '&.Mui-selected': {
              background: 'linear-gradient(135deg, #0EA5E9 0%, #7DA4D9 100%)',
              color: '#fff',
            },
          }}
        >
          Occupancy
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Chart card */}
      <Card sx={{ borderRadius: RADIUS.xl, mb: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {totalLabel}
            </Typography>
            <Typography variant="h6" fontWeight={800}>
              {total}
            </Typography>
          </Box>

          <BarChart
            values={values}
            maxValue={maxValue}
            color={color}
            formatLabel={formatLabel}
          />
          <DayLabels days={MOCK_ANALYTICS.days} />
        </CardContent>
      </Card>

      {/* Quick insight cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
        <Card sx={{ borderRadius: RADIUS.xl }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="caption" color="text.secondary">
              Best day
            </Typography>
            <Typography variant="subtitle1" fontWeight={800}>
              {isRevenue
                ? MOCK_ANALYTICS.days[MOCK_ANALYTICS.revenue.indexOf(Math.max(...MOCK_ANALYTICS.revenue))]
                : MOCK_ANALYTICS.days[MOCK_ANALYTICS.occupancy.indexOf(Math.max(...MOCK_ANALYTICS.occupancy))]}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: RADIUS.xl }}>
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="caption" color="text.secondary">
              Peak value
            </Typography>
            <Typography variant="subtitle1" fontWeight={800}>
              {formatLabel(maxValue)}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default DemoAnalyticsTab;
